<template>
  <div>
    <div
      class="absolute bottom-[-44px] right-[-21px] z-[15] flex items-center px-5 pb-5 pt-[2px]"
      :class="{
        'flex-col': isText && columnWidth < 20,
        '!right-[-28px]':
          isText && openAiStore.openAiEnabled
            ? columnWidth < 20
            : columnWidth < 17,
      }"
    >
      <span
        v-if="
          isMovableForCurrentPermissions &&
          isText &&
          !openAiStore.openAiEnabled &&
          generalStore.type === 'standalone'
        "
        v-tooltip="{
          content: 'Only PRO version',
        }"
        class="grid items-center rounded-bl-[3px] bg-content-1 px-1 py-0.5 !text-white/80 transition duration-300"
        style="filter: saturate(0.5)"
      >
        <AIIconMono />
      </span>
      <aiContextMenu
        v-if="
          isMovableForCurrentPermissions &&
          (isText || isButton) &&
          openAiStore.openAiEnabled
        "
        :is-last-column="isLastColumn"
        :right="columnWidth < 20"
        :translate-only="isButton"
        @enrich="enrichTextWithAi"
      ></aiContextMenu>

      <div
        class="flex cursor-pointer items-center overflow-hidden rounded-br-[3px]"
        :class="{
          'flex-col rounded-b-[3px]':
            isText && openAiStore.openAiEnabled
              ? columnWidth < 20
              : columnWidth < 17,
          'rounded-b-[3px]': !(isText || isButton),
        }"
      >
        <a
          v-if="isMovableForCurrentPermissions"
          v-tooltip.top="{
            content: $t(duplicateAndCopyTooltip),
            html: true,
          }"
          data-testid="duplicate-block"
          class="grid items-center bg-content-1 px-1 py-0.5 !text-white transition duration-300 hover:bg-content-2"
          @click.stop="copyOrDuplicate"
        >
          <Pipe v-if="copied" />
          <Copy v-else />
        </a>
        <a
          v-if="isMovableForCurrentPermissions"
          v-tooltip.top="{ content: $t('file_manager.delete') }"
          data-testid="delete-block"
          class="grid items-center bg-content-1 px-1 py-0.5 !text-white transition duration-300 hover:bg-destructive-2 hover:!text-white"
          @click.stop="deleteBlock"
        >
          <Delete />
        </a>
      </div>
    </div>
    <div
      v-if="isLocked || commentsStore.enableComments"
      class="absolute right-[-17px] top-[-40px] z-[50] flex items-end px-4 pt-4 font-inter text-[9px] font-semibold"
      :class="[isLocked ? 'is-locked' : '']"
    >
      <a
        v-if="commentsStore.enableComments"
        v-tooltip.top="{ content: $t('editor.comment') }"
        data-testid="comment-block"
        class="grid cursor-pointer items-center rounded-t-[3px] bg-content-1 px-1 py-0.5 text-white transition duration-300 hover:bg-content-2"
        @click.stop="commentSection"
      >
        <Comments />
      </a>
      <div
        v-if="isLocked"
        class="mt-[8px] grid items-center justify-center rounded-t-[3px] bg-content-1 px-1 py-0.5 leading-3 text-white"
      >
        <svg
          data-testid="content-block-lock"
          style="width: 10px; height: 12px"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
            clip-rule="evenodd"
          ></path>
        </svg>
      </div>
    </div>
    <addBlock
      :block-width="blockWidth"
      :column-padding-x="columnPaddingX"
      :show-paste="showPaste"
    ></addBlock>
    <arrowMovement
      v-if="isMovableForCurrentPermissions"
      :disable-down-movement="disableDownMovement"
      :disable-up-movement="disableUpMovement"
      block
      @doUpMovement="$emit('moveUp')"
      @doDownMovement="$emit('moveDown')"
    ></arrowMovement>

    <div
      v-if="!isLocked && !isLastColumn && !isMobileView"
      :id="`handle-${sectionIndex}-${columnIndex}`"
      :data-blockindex="blockIndex"
      class="absolute right-0 top-[50%] z-[55] pr-4"
      style="transform: translate(100%, -50%)"
      :class="{
        'top-[20px]':
          isText && openAiStore.openAiEnabled
            ? columnWidth < 20
            : columnWidth < 17,
      }"
    >
      <div
        v-if="showWidth || resizeColumnsStore.isResizing"
        class="absolute left-0 top-[-4px] grid h-5 w-8 items-center justify-center rounded !bg-black font-inter text-[11px] font-semibold leading-[1] text-white"
        style="transform: translate(-50%, -100%)"
      >
        <span> {{ Math.round(Number(columnWidth)) }}%</span>
      </div>
      <div
        class="group/resizer box-border flex w-[9px] cursor-ew-resize items-center rounded-r-[3px] bg-content-1 py-1 pl-[5px] pr-[3px] text-[8px] text-white transition-all duration-300 hover:w-3 hover:gap-[2px] hover:bg-content-2"
        :class="{
          'w-3 gap-[2px]': resizeColumnsStore.isResizing,
        }"
        @mouseover="showWidth = true"
        @mouseleave="showWidth = false"
      >
        <div class="h-6 w-[1px] bg-white"></div>
        <div
          class="h-6 w-[0px] bg-white transition-all duration-300 group-hover/resizer:w-[1px]"
          :class="{
            '!w-[1px]': resizeColumnsStore.isResizing,
          }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import addBlock from "./addBlock.vue";
  import arrowMovement from "./arrowMovement.vue";
  import aiContextMenu from "./aiContextMenu.vue";

  import { useRolesStore } from "@/stores/roles";
  import { useOpenAiStore } from "@/stores/openAiStore";
  import { useGeneralStore } from "@/stores/generalStore";
  import { useCommentsStore } from "@/stores/commentsStore";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useResizeColumnsStore } from "@/stores/resizeColumnsStore";

  import { useClipboard } from "@vueuse/core";
  import { Copy, AIIconMono, Comments, Delete, Pipe } from "@topol/icons";

  import { computed, getCurrentInstance, ref } from "vue";

  defineProps({
    product: Boolean,
    isHtml: {
      type: Boolean,
      default: false,
    },
    isPlaceholder: {
      type: Boolean,
      default: false,
    },
    blockWidth: {
      type: Number,
      required: true,
    },
    disableUpMovement: Boolean,
    disableDownMovement: Boolean,
    isActive: Boolean,
    columnPaddingX: {
      type: Number,
      required: false,
      default: undefined,
    },
    columnWidth: {
      type: Number,
      required: true,
    },
    isText: {
      type: Boolean,
      default: false,
    },
    isButton: {
      type: Boolean,
      default: false,
    },
    isLastColumn: {
      type: Boolean,
      required: true,
    },
    sectionIndex: {
      type: Number,
      required: true,
    },
    columnIndex: {
      type: Number,
      required: true,
    },
    blockIndex: {
      type: Number,
      required: true,
    },
    showPaste: {
      type: Boolean,
      default: false,
    },
    copied: {
      type: Boolean,
      default: false,
    },
  });

  const emits = defineEmits([
    "moveUp",
    "moveDown",
    "duplicate",
    "remove",
    "enrich",
    "copy",
  ]);

  const rolesStore = useRolesStore();
  const openAiStore = useOpenAiStore();
  const generalStore = useGeneralStore();
  const commentsStore = useCommentsStore();
  const mjmlStore = useMjmlStore();
  const resizeColumnsStore = useResizeColumnsStore();
  const { isSupported } = useClipboard();
  const showWidth = ref(false);

  const instance = getCurrentInstance();

  const isLocked = computed(() => {
    //@ts-expect-error
    return instance.parent.props.block.locked &&
      //@ts-expect-error
      instance.parent.props.block.locked
      ? //@ts-expect-error
        instance.parent.props.block.locked
      : false;
  });

  const isMovableForCurrentPermissions = computed(() => {
    if (
      !isLocked.value ||
      (isLocked.value && rolesStore.permissions.canEditLockedBlocks)
    ) {
      return true;
    }

    return false;
  });

  const isMobileView = computed(() => {
    return mjmlStore.isMobileView;
  });

  const isMac = computed(() => {
    return navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  });

  const duplicateAndCopyTooltip = computed(() => {
    if (!isSupported.value) {
      return "editor.duplicate";
    }

    if (isMac.value) {
      return "editor.duplicate_copy_mac";
    }

    return "editor.duplicate_copy";
  });

  const deleteBlock = () => {
    emits("remove");
  };

  const enrichTextWithAi = () => {
    emits("enrich");
  };

  function copyOrDuplicate(event: any) {
    if ((isMac.value && event.metaKey) || (!isMac.value && event.ctrlKey)) {
      if (isSupported.value) {
        emits("copy");
      }
    } else {
      emits("duplicate");
    }
  }

  function commentSection() {
    mjmlStore.selectBlock({
      //@ts-expect-error
      sectionIndex: instance.parent.props.sectionIndex,
      //@ts-expect-error
      columnIndex: instance.parent.props.columnIndex,
      //@ts-expect-error
      blockIndex: instance.parent.props.blockIndex,
    });
    commentsStore.openDetail = true;
    commentsStore.commentsOpen = true;
  }
</script>

<style lang="scss" scoped>
  .content-label {
    background-color: #00d7ca;
    color: white;
    position: absolute;
    z-index: 50;
    display: flex;
    align-items: center;
    right: -1px;
    font-family: Inter;
    font-size: 9px;
    line-height: 100%;
    font-weight: 600;
    text-transform: lowercase;
    padding: 4px;
    top: 0;
    margin-top: -17px;
    margin-right: 0px;
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;

    &.is-locked {
      top: -2px;
    }
  }

  .tool-icons-container {
    position: absolute;
    top: calc(50% - 30px);
    right: -13px;
    z-index: 51 !important;
  }

  .tool-icons-container a {
    margin-top: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 999px;
    width: 26px;
    height: 26px;
    background-color: #3c4048;
    box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.25);
    color: #fff !important;
    cursor: pointer;
  }

  .tool-icons-container a:hover {
    background-color: #13171e;
    transition: background-color 150ms linear;
  }

  .tool-icons-container .delete:hover {
    background-color: #fa5151;
    transition: background-color 150ms linear;
  }
</style>
